import { ResourceHubContentType } from '@models/section/featureCardSection';
import { TaggedSlugEntityType } from '@models/tag';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { getVideoThumbnailImage } from '@helpers/videoDriveKeyHelper';
import ebookPlaceholder from '@images/PageContentType/ebook-placeholder.png';
import webinarPlaceholder from '@images/PageContentType/webinar-placeholder.png';
import podcastPlaceholder from '@images/PageContentType/podcast-placeholder.png';
import toolsPlaceholder from '@images/PageContentType/tools-placeholder.png';

const isPageType = (entityType: ResourceHubContentType): boolean => {
    return (
        entityType === ResourceHubContentType.Ebook ||
        entityType === ResourceHubContentType.Webinar ||
        entityType === ResourceHubContentType.Podcast ||
        entityType === ResourceHubContentType.Guides ||
        entityType === ResourceHubContentType.Tools
    );
};

const entityTypeRouteTypeMap: Partial<Record<ResourceHubContentType, keyof typeof languageRouteTypes>> = {
    [ResourceHubContentType.Blog]: 'BLOG_POST',
    [ResourceHubContentType.SuccessStory]: 'CASE_STUDY',
    [ResourceHubContentType.PressRelease]: 'PRESS_RELEASE',
    [ResourceHubContentType.WhitePaper]: 'WHITE_PAPER',
    [ResourceHubContentType.Glossary]: 'GLOSSARY',
    [ResourceHubContentType.Video]: 'VIDEO',
};

export const getCardLinkPath = (
    card: TaggedSlugEntityType,
    selectedLanguage: string,
    entityType?: ResourceHubContentType
): string => {
    if (entityType) {
        if (isPageType(entityType)) {
            return `/${card.slug}`;
        }
        const routeKey = entityTypeRouteTypeMap[entityType];
        if (routeKey) {
            return `${getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes[routeKey])}${card.slug}`;
        }
    }

    return `/${card.slug}`;
};

export const getImagePath = (card: TaggedSlugEntityType, prefixStringWithBaseUrl: (url: string) => string, entityType?: ResourceHubContentType): string | undefined => {
    const placeholderMap: Partial<Record<ResourceHubContentType, string>> = {
        [ResourceHubContentType.Ebook]: ebookPlaceholder,
        [ResourceHubContentType.Webinar]: webinarPlaceholder,
        [ResourceHubContentType.Podcast]: podcastPlaceholder,
        [ResourceHubContentType.Tools]: toolsPlaceholder,
    };

    const placeholderImage = entityType ? placeholderMap[entityType] : undefined;
    if (placeholderImage) {
        return placeholderImage;
    }

    if (entityType === ResourceHubContentType.Video) {
        const imagePath = 'cardImageUrl' in card ? card.cardImageUrl : undefined;
        if (imagePath) {
            return prefixStringWithBaseUrl(imagePath) || undefined;
        }
        const videoKey = 'videoKey' in card ? card.videoKey : undefined;
        const videoType = 'videoType' in card ? card.videoType : undefined;
        return getVideoThumbnailImage(videoKey, videoType) || undefined;
    }

    if ('cardImageUrl' in card && card.cardImageUrl) {
        return prefixStringWithBaseUrl(card.cardImageUrl) || undefined;
    }

    if ('imageUrl' in card && card.imageUrl) {
        return prefixStringWithBaseUrl(card.imageUrl) || undefined;
    }

    return undefined;
};

export const getImageAlt = (card: TaggedSlugEntityType): string => {
    if ('cardImageAlt' in card && card.cardImageAlt) {
        return card.cardImageAlt;
    } else if ('imageAlt' in card && card.imageAlt) {
        return card.imageAlt;
    }
    return '';
};

export const getPillUrl = (selectedLanguage: string, entityType?: ResourceHubContentType): string => {
    const routeMap: Partial<Record<ResourceHubContentType, keyof typeof languageRouteTypes>> = {
        [ResourceHubContentType.Blog]: 'BLOG_HOME',
        [ResourceHubContentType.SuccessStory]: 'SUCCESS_STORIES_HOME',
        [ResourceHubContentType.WhitePaper]: 'WHITE_PAPERS_HOME',
        [ResourceHubContentType.Glossary]: 'GLOSSARY',
        [ResourceHubContentType.PressRelease]: 'NEWS_AND_VIEWS_URL',
        [ResourceHubContentType.Ebook]: 'EBOOK',
        [ResourceHubContentType.Webinar]: 'WEBNINAR',
        [ResourceHubContentType.Podcast]: 'PODCAST',
        [ResourceHubContentType.Tools]: 'TOOLS',
        [ResourceHubContentType.Guides]: 'GUIDES',
        [ResourceHubContentType.Video]: 'VIDEO_HOME',
    };

    const routeKey = entityType ? routeMap[entityType] : undefined;
    return routeKey ? getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes[routeKey]) : '';
};